/**
 * OnboardingCommandCenter — Platform-admin onboarding pipeline.
 *
 * Mounted inside /platform Operations Console. Talks to:
 *   GET    /api/onboarding
 *   GET    /api/onboarding/{record_id}
 *   PATCH  /api/onboarding/{record_id}
 *   PATCH  /api/onboarding/{record_id}/checklist/{code}
 *   POST   /api/onboarding/{record_id}/notes
 *   POST   /api/onboarding/{record_id}/tasks
 *   PATCH  /api/onboarding/tasks/{task_id}
 *   GET    /api/onboarding/dashboard/funnel
 *
 * Surfaces: Dashboard tiles, lifecycle funnel, filterable record list,
 * detail drawer with status engine, checklist, timeline, notes, tasks.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import {
  Activity, AlertTriangle, Calendar, CheckCircle2, ClipboardList,
  Circle, Loader2, RefreshCw, Search, Send, X, Plus, ShieldAlert,
  Building2, Workflow, BadgeCheck, Hourglass, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { HealthChip } from "@/components/admin/CustomerHealthPanel";

const STATUS_COLORS = {
  not_started:        "#9CA3AF",
  welcome_sent:       "#7dd3fc",
  owner_invited:      "#7dd3fc",
  owner_activated:    "#7dd3fc",
  company_setup:      "#fbbf24",
  user_provisioning:  "#fbbf24",
  project_setup:      "#fbbf24",
  plan_upload:        "#fbbf24",
  training_scheduled: "#fb923c",
  training_complete:  "#fb923c",
  go_live_review:     "#fb923c",
  live:               "#86efac",
  blocked:            "#fca5a5",
  paused:             "#cbd5e1",
  cancelled:          "#9CA3AF",
};

const PRIORITY_COLORS = {
  low:      { bg: "rgba(107,114,128,0.10)", border: "rgba(107,114,128,0.35)", text: "#cbd5e1" },
  medium:   { bg: "rgba(56,189,248,0.10)",  border: "rgba(56,189,248,0.35)",  text: "#7dd3fc" },
  high:     { bg: "rgba(249,115,22,0.12)",  border: "rgba(249,115,22,0.40)",  text: "#fb923c" },
  critical: { bg: "rgba(220,38,38,0.10)",   border: "rgba(220,38,38,0.40)",   text: "#fca5a5" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return "—"; }
};
const fmtDay = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
};
const titleCase = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function OnboardingCommandCenter() {
  const [records, setRecords] = useState([]);
  const [counts, setCounts] = useState(null);
  const [statuses, setStatuses] = useState({});
  const [funnel, setFunnel] = useState(null);
  const [healthByTenant, setHealthByTenant] = useState({});
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.q = filter;
      if (statusFilter) params.status = statusFilter;
      const [{ data }, fnl, health] = await Promise.all([
        api.get("/onboarding", { params }),
        api.get("/onboarding/dashboard/funnel"),
        api.get("/customer-health"),
      ]);
      setRecords(data.records || []);
      setCounts(data.counts || null);
      setStatuses(data.statuses || {});
      setFunnel(fnl.data);
      const map = {};
      for (const h of (health.data?.records || [])) {
        map[h.tenant_id] = h;
      }
      setHealthByTenant(map);
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Failed to load onboarding records");
    } finally {
      setLoading(false);
    }
  }, [filter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div
      className="rounded-xl border bg-[var(--fop-surface)]"
      style={{ borderColor: "var(--fop-border)" }}
      data-testid="onboarding-center"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b flex items-center justify-between gap-3 flex-wrap"
           style={{ borderColor: "var(--fop-border)" }}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md inline-flex items-center justify-center"
               style={{ background: "rgba(249, 115, 22, 0.10)", border: "1px solid rgba(249, 115, 22, 0.35)" }}>
            <Workflow size={16} className="text-[var(--fop-primary)]" />
          </div>
          <div>
            <div className="text-[var(--fop-text)] font-semibold">Customer Onboarding</div>
            <div className="text-xs text-[var(--fop-text-3)] font-mono uppercase tracking-wider">
              Pipeline · platform admin only
            </div>
          </div>
        </div>
        <button onClick={load} className="btn btn-ghost"
                style={{ minHeight: "36px", padding: "0 12px" }}
                data-testid="onboarding-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Dashboard tiles + lifecycle funnel */}
      {counts && funnel && (
        <div className="p-5 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] gap-5 border-b"
             style={{ borderColor: "var(--fop-border)" }}>
          <div className="grid grid-cols-2 gap-2.5">
            <Tile testid="tile-active"     icon={Activity}    label="Active"      value={counts.active} />
            <Tile testid="tile-blocked"    icon={AlertTriangle} tone="danger" label="Blocked" value={counts.blocked} />
            <Tile testid="tile-week"       icon={Calendar}    label="Go-live · 7d"  value={counts.going_live_week} />
            <Tile testid="tile-month"      icon={Calendar}    label="Go-live · 30d" value={counts.going_live_month} />
            <Tile testid="tile-completed"  icon={BadgeCheck} tone="ok"     label="Live · 30d"    value={counts.completed_30d} />
            <Tile testid="tile-avg-days"   icon={Hourglass}   label="Avg days→live" value={counts.avg_days_to_go_live ?? "—"} />
          </div>

          <LifecycleFunnel funnel={funnel} />
        </div>
      )}

      {/* Status filter + search */}
      <div className="px-5 py-3 border-b flex gap-3 flex-wrap"
           style={{ borderColor: "var(--fop-border)" }}>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fop-text-3)]" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search company / owner / email…"
            className="w-full h-10 pl-9 pr-3 rounded-md text-sm"
            data-testid="onboarding-search"
          />
        </div>
      </div>

      {counts && (
        <div className="px-5 py-3 border-b flex flex-wrap gap-2 text-[11px] font-mono uppercase tracking-wider"
             style={{ borderColor: "var(--fop-border)" }}>
          <FilterChip label="All"    value={counts.total}   active={statusFilter === ""} onClick={() => setStatusFilter("")} testid="filter-all" />
          {Object.entries(statuses).map(([code, label]) => (
            <FilterChip
              key={code}
              label={label}
              value={counts[code] || 0}
              active={statusFilter === code}
              onClick={() => setStatusFilter(statusFilter === code ? "" : code)}
              testid={`filter-${code}`}
              color={STATUS_COLORS[code]}
            />
          ))}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[var(--fop-text-3)]" /></div>
      ) : records.length === 0 ? (
        <div className="p-12 text-center text-[var(--fop-text-3)] text-sm" data-testid="onboarding-empty">
          <ClipboardList className="w-7 h-7 text-[var(--fop-text-4)] mx-auto mb-3" />
          {statusFilter || filter
            ? "No onboarding records match the current filter."
            : "No onboarding records yet. Convert a lead to start the pipeline."}
        </div>
      ) : (
        <div className="divide-y" style={{ borderColor: "var(--fop-border)" }}>
          {records.map((r) => (
            <RecordRow key={r.id} record={r} health={healthByTenant[r.tenant_id]} onOpen={() => setActive(r)} />
          ))}
        </div>
      )}

      {active && (
        <OnboardingDrawer
          recordId={active.id}
          statuses={statuses}
          onClose={() => setActive(null)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
}

/* =================================================================
 * Tiles
 * =============================================================== */
function Tile({ icon: Icon, label, value, tone, testid }) {
  const accent =
    tone === "danger" ? "rgba(220, 38, 38, 0.5)" :
    tone === "ok"     ? "rgba(34, 197, 94, 0.55)" :
                        "rgba(249, 115, 22, 0.45)";
  return (
    <div
      data-testid={testid}
      className="rounded-md p-3.5"
      style={{
        background: "var(--fop-surface-2)",
        border: `1px solid var(--fop-border)`,
        borderLeft: `3px solid ${accent}`,
      }}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon size={13} className="text-[var(--fop-text-3)]" />
        <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">{label}</div>
      </div>
      <div className="font-display text-2xl text-[var(--fop-text)]">{value}</div>
    </div>
  );
}

/* =================================================================
 * Lifecycle Funnel
 * =============================================================== */
function LifecycleFunnel({ funnel }) {
  const max = Math.max(1, ...funnel.stages.map((s) => s.count || 0));
  return (
    <div className="rounded-md p-4"
         style={{ background: "var(--fop-surface-2)", border: "1px solid var(--fop-border)" }}
         data-testid="lifecycle-funnel">
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--fop-text-3)] mb-3">
        Customer lifecycle
      </div>
      <div className="space-y-1.5">
        {funnel.stages.map((s) => {
          const pct = Math.round(((s.count || 0) / max) * 100);
          return (
            <div key={s.key} className="grid grid-cols-[100px_minmax(0,1fr)_44px] items-center gap-3"
                 data-testid={`funnel-stage-${s.key}`}>
              <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-2)]">{s.label}</div>
              <div className="relative h-2.5 rounded-full overflow-hidden"
                   style={{ background: "var(--fop-surface-3)", border: "1px solid var(--fop-border)" }}>
                <div className="h-full rounded-full transition-[width] duration-500"
                     style={{
                       width: `${pct}%`,
                       background: "linear-gradient(90deg, var(--fop-primary), #f59e0b)",
                     }} />
              </div>
              <div className="text-sm font-semibold text-[var(--fop-text)] text-right">{s.count || 0}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FilterChip({ label, value, active, onClick, testid, color }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="px-2.5 py-1 rounded transition-colors"
      style={{
        background: active ? "var(--fop-surface-3)" : "var(--fop-surface-2)",
        border: `1px solid ${active ? "var(--fop-primary)" : "var(--fop-border)"}`,
        color: color || "var(--fop-text-2)",
      }}
    >
      {label} · {value}
    </button>
  );
}

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || "#cbd5e1";
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono uppercase tracking-wider"
      style={{ background: `${c}1a`, border: `1px solid ${c}66`, color: c }}
    >
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

function ProgressBar({ percent }) {
  return (
    <div className="relative h-1.5 w-full rounded-full overflow-hidden"
         style={{ background: "var(--fop-surface-3)" }}>
      <div className="h-full rounded-full"
           style={{
             width: `${Math.max(0, Math.min(100, percent || 0))}%`,
             background: "linear-gradient(90deg, var(--fop-primary), #f59e0b)",
             transition: "width 0.4s ease",
           }} />
    </div>
  );
}

function RecordRow({ record, health, onOpen }) {
  const pct = record.progress_percent ?? 0;
  return (
    <button
      onClick={onOpen}
      data-testid={`onboarding-row-${record.id}`}
      className="w-full text-left px-5 py-4 hover:bg-[var(--fop-surface-2)] transition-colors"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-semibold text-[var(--fop-text)] truncate">{record.company_name}</span>
            <StatusBadge status={record.status} />
            {health && (
              <HealthChip status={health.health_status} score={health.health_score} />
            )}
            {record.assigned_manager_email && (
              <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                Mgr · {record.assigned_manager_email}
              </span>
            )}
          </div>
          <div className="text-sm text-[var(--fop-text-2)] truncate">
            {record.owner_email || "no owner"} {record.target_go_live_date && <span className="text-[var(--fop-text-3)]"> · target {fmtDay(record.target_go_live_date)}</span>}
          </div>
          <div className="mt-2 max-w-md"><ProgressBar percent={pct} /></div>
        </div>
        <div className="text-right text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] shrink-0">
          <div className="text-[var(--fop-text)] font-semibold text-base">{pct}%</div>
          <div>Last activity</div>
          <div>{fmtDate(record.last_activity_at)}</div>
        </div>
      </div>
    </button>
  );
}

/* =================================================================
 * Drawer
 * =============================================================== */
function OnboardingDrawer({ recordId, statuses, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskPriority, setTaskPriority] = useState("medium");
  const [taskAssigned, setTaskAssigned] = useState("");
  const [taskDue, setTaskDue] = useState("");
  const [mgr, setMgr] = useState("");
  const [goLive, setGoLive] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/onboarding/${recordId}`);
      setData(data);
      setMgr(data.record?.assigned_manager_email || "");
      setGoLive((data.record?.target_go_live_date || "").slice(0, 10));
      // Health (best-effort, separate call)
      try {
        const h = await api.get(`/customer-health/${data.record.tenant_id}`);
        setHealth(h.data.health);
      } catch { /* non-critical */ }
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Failed to load record");
    } finally {
      setLoading(false);
    }
  }, [recordId]);

  useEffect(() => { load(); }, [load]);

  const propagate = (record) => {
    setData((d) => d ? { ...d, record } : d);
    onChanged?.();
  };

  const setStatus = async (status) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/onboarding/${recordId}`, { status });
      propagate(data.record);
      toast.success(`Status → ${titleCase(status)}`);
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Failed");
    } finally {
      setSaving(false);
    }
  };

  const saveMgrGoLive = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/onboarding/${recordId}`, {
        assigned_manager_email: mgr,
        target_go_live_date: goLive || null,
      });
      propagate(data.record);
      toast.success("Saved");
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleChecklist = async (code, patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/onboarding/${recordId}/checklist/${code}`, patch);
      propagate(data.record);
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Failed");
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSaving(true);
    try {
      await api.post(`/onboarding/${recordId}/notes`, { text: noteText.trim() });
      setNoteText("");
      await load();
      toast.success("Note added");
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Failed");
    } finally {
      setSaving(false);
    }
  };

  const addTask = async () => {
    if (!taskTitle.trim()) return;
    setSaving(true);
    try {
      await api.post(`/onboarding/${recordId}/tasks`, {
        title: taskTitle.trim(),
        assigned_to: taskAssigned.trim() || null,
        priority: taskPriority,
        due_date: taskDue || null,
      });
      setTaskTitle(""); setTaskAssigned(""); setTaskDue(""); setTaskPriority("medium");
      await load();
      toast.success("Task created");
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Failed");
    } finally {
      setSaving(false);
    }
  };

  const setTaskStatus = async (taskId, status) => {
    setSaving(true);
    try {
      await api.patch(`/onboarding/tasks/${taskId}`, { status });
      await load();
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[180] flex items-stretch justify-end"
      style={{ background: "rgba(2, 6, 23, 0.65)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="onboarding-drawer"
    >
      <div
        className="w-full max-w-2xl h-full bg-[var(--fop-surface)] border-l border-[var(--fop-border)] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {loading || !data?.record ? (
          <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[var(--fop-text-3)]" /></div>
        ) : (
          <>
            {/* Header */}
            <div className="px-6 py-5 border-b flex items-start justify-between gap-4"
                 style={{ borderColor: "var(--fop-border)" }}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 size={14} className="text-[var(--fop-text-3)]" />
                  <div className="font-display text-xl text-[var(--fop-text)] truncate">{data.record.company_name}</div>
                </div>
                <div className="text-sm text-[var(--fop-text-2)] truncate">
                  {data.record.owner_name || "—"} {data.record.owner_email && <>· <a href={`mailto:${data.record.owner_email}`} className="text-[var(--fop-primary)] hover:underline">{data.record.owner_email}</a></>}
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <StatusBadge status={data.record.status} />
                  <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                    {data.record.progress_percent ?? 0}% complete
                  </span>
                  {data.record.target_go_live_date && (
                    <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                      Target {fmtDay(data.record.target_go_live_date)}
                    </span>
                  )}
                </div>
                <div className="mt-3 max-w-xs"><ProgressBar percent={data.record.progress_percent} /></div>
              </div>
              <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
                      data-testid="onboarding-drawer-close">
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-7">
              {health && (
                <Section title="Customer health">
                  <div className="rounded-lg p-3 flex items-start gap-3"
                       style={{
                         background: "var(--fop-surface-2)",
                         border: "1px solid var(--fop-border)",
                       }}
                       data-testid="drawer-health">
                    <div className="shrink-0">
                      <HealthChip status={health.health_status} score={health.health_score} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm text-[var(--fop-text)]">{health.recommended_action}</div>
                      <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mt-1">
                        {(health.positive_signals || []).length} positive · {(health.risk_flags || []).length} risk · updated {fmtDate(health.updated_at)}
                      </div>
                    </div>
                  </div>
                </Section>
              )}

              {/* Status engine */}
              <Section title="Status">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(statuses).map(([code, label]) => {
                    const selected = data.record.status === code;
                    const c = STATUS_COLORS[code];
                    return (
                      <button
                        key={code}
                        disabled={saving}
                        onClick={() => setStatus(code)}
                        data-testid={`onboarding-status-${code}`}
                        className="px-2.5 py-1 rounded text-[11px] font-mono uppercase tracking-wider"
                        style={{
                          background: selected ? `${c}26` : "var(--fop-surface-2)",
                          border: `1px solid ${selected ? c : "var(--fop-border)"}`,
                          color: selected ? c : "var(--fop-text-2)",
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </Section>

              {/* Assignment */}
              <Section title="Assignment & target go-live">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Assigned onboarding manager">
                    <input value={mgr} onChange={(e) => setMgr(e.target.value)}
                           placeholder="email@forgedops.com"
                           data-testid="onboarding-mgr" />
                  </Field>
                  <Field label="Target go-live date">
                    <input type="date" value={goLive} onChange={(e) => setGoLive(e.target.value)}
                           data-testid="onboarding-go-live" />
                  </Field>
                </div>
                <button onClick={saveMgrGoLive} disabled={saving}
                        className="btn btn-primary mt-3"
                        style={{ minHeight: "40px", padding: "0 14px" }}
                        data-testid="onboarding-save-mgr">
                  Save
                </button>
              </Section>

              {/* Checklist */}
              <Section title="Checklist">
                <div className="space-y-1.5">
                  {(data.record.checklist || []).map((item) => (
                    <ChecklistRow key={item.code} item={item} onToggle={toggleChecklist} disabled={saving} />
                  ))}
                </div>
              </Section>

              {/* Tasks */}
              <Section title="Tasks">
                <div className="space-y-2">
                  {(data.tasks || []).length === 0 ? (
                    <div className="text-sm text-[var(--fop-text-3)]">No tasks yet.</div>
                  ) : (
                    data.tasks.map((t) => (
                      <TaskRow key={t.id} task={t} onSetStatus={setTaskStatus} disabled={saving} />
                    ))
                  )}
                </div>
                <div className="mt-4 rounded-md p-3 space-y-2"
                     style={{ background: "var(--fop-surface-2)", border: "1px solid var(--fop-border)" }}>
                  <input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)}
                         placeholder="Task title"
                         data-testid="task-title" />
                  <div className="grid sm:grid-cols-3 gap-2">
                    <input value={taskAssigned} onChange={(e) => setTaskAssigned(e.target.value)}
                           placeholder="assigned to"
                           data-testid="task-assigned" />
                    <select value={taskPriority} onChange={(e) => setTaskPriority(e.target.value)}
                            data-testid="task-priority">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                    <input type="date" value={taskDue} onChange={(e) => setTaskDue(e.target.value)}
                           data-testid="task-due" />
                  </div>
                  <button disabled={saving || !taskTitle.trim()}
                          onClick={addTask}
                          className="btn btn-primary"
                          style={{ minHeight: "40px", padding: "0 14px" }}
                          data-testid="task-add">
                    <Plus size={14} /> Add task
                  </button>
                </div>
              </Section>

              {/* Notes */}
              <Section title="Internal notes">
                <div className="space-y-2 mb-3">
                  {(data.record.notes || []).length === 0 ? (
                    <div className="text-sm text-[var(--fop-text-3)]">No notes yet.</div>
                  ) : (
                    (data.record.notes || []).map((n) => (
                      <div key={n.id}
                           className="rounded-md p-3 text-sm"
                           style={{ background: "var(--fop-surface-2)", border: "1px solid var(--fop-border)" }}>
                        <div className="text-[var(--fop-text)] whitespace-pre-wrap">{n.text}</div>
                        <div className="mt-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                          {n.author_email || "—"} · {fmtDate(n.at)}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <textarea rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)}
                          placeholder="Internal note — visible only to ForgedOps staff"
                          data-testid="onboarding-note-text"
                          style={{ minHeight: "70px", resize: "vertical" }} />
                <button disabled={saving || !noteText.trim()}
                        onClick={addNote}
                        className="btn btn-primary mt-2"
                        style={{ minHeight: "40px", padding: "0 14px" }}
                        data-testid="onboarding-add-note">
                  <Send size={14} /> Add note
                </button>
              </Section>

              {/* Timeline */}
              <Section title="Timeline">
                <Timeline events={data.record.timeline || []} />
              </Section>

              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-4)] pt-4 border-t"
                   style={{ borderColor: "var(--fop-border)" }}>
                Tenant ID: {data.record.tenant_id} · Record ID: {data.record.id}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ChecklistRow({ item, onToggle, disabled }) {
  return (
    <div
      className="rounded-md px-3 py-2 flex items-center gap-3"
      style={{ background: "var(--fop-surface-2)", border: "1px solid var(--fop-border)" }}
      data-testid={`checklist-${item.code}`}
    >
      <button
        disabled={disabled}
        onClick={() => onToggle(item.code, { complete: !item.complete })}
        title={item.complete ? "Mark incomplete" : "Mark complete"}
        data-testid={`checklist-toggle-${item.code}`}
        className="shrink-0"
      >
        {item.complete
          ? <CheckCircle2 size={18} className="text-[var(--fop-success)]" />
          : item.blocked
            ? <ShieldAlert size={18} className="text-[var(--fop-danger)]" />
            : <Circle size={18} className="text-[var(--fop-text-3)]" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${item.complete ? "text-[var(--fop-text-3)] line-through" : "text-[var(--fop-text)]"}`}>
          {item.label}
        </div>
        {(item.notes || item.completed_by) && (
          <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5 truncate">
            {item.completed_by && <>by {item.completed_by} · {fmtDate(item.completed_at)}</>}
            {item.notes && <span className="normal-case tracking-normal"> · {item.notes}</span>}
          </div>
        )}
      </div>
      <button
        disabled={disabled}
        onClick={() => onToggle(item.code, { blocked: !item.blocked })}
        title={item.blocked ? "Unblock" : "Mark blocked"}
        data-testid={`checklist-block-${item.code}`}
        className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded"
        style={{
          background: item.blocked ? "rgba(220,38,38,0.10)" : "transparent",
          border: `1px solid ${item.blocked ? "rgba(220,38,38,0.45)" : "var(--fop-border)"}`,
          color: item.blocked ? "#fca5a5" : "var(--fop-text-3)",
        }}
      >
        {item.blocked ? "Blocked" : "Block"}
      </button>
    </div>
  );
}

function TaskRow({ task, onSetStatus, disabled }) {
  const c = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
  return (
    <div
      className="rounded-md p-3 flex items-start gap-3"
      style={{ background: "var(--fop-surface-2)", border: "1px solid var(--fop-border)" }}
      data-testid={`task-row-${task.id}`}
    >
      <button
        disabled={disabled}
        onClick={() => onSetStatus(task.id, task.status === "done" ? "open" : "done")}
        className="shrink-0 mt-0.5"
        data-testid={`task-toggle-${task.id}`}
      >
        {task.status === "done"
          ? <CheckCircle2 size={18} className="text-[var(--fop-success)]" />
          : <Circle size={18} className="text-[var(--fop-text-3)]" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className={`text-sm ${task.status === "done" ? "text-[var(--fop-text-3)] line-through" : "text-[var(--fop-text)]"}`}>{task.title}</span>
          <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded"
                style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text }}>
            {task.priority}
          </span>
        </div>
        <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
          {task.assigned_to ? `→ ${task.assigned_to} · ` : ""}
          {task.due_date ? `due ${fmtDay(task.due_date)}` : "no due date"}
        </div>
      </div>
    </div>
  );
}

function Timeline({ events }) {
  if (!events?.length) return <div className="text-sm text-[var(--fop-text-3)]">No events yet.</div>;
  return (
    <ol className="space-y-2" data-testid="onboarding-timeline">
      {[...events].reverse().map((ev) => (
        <li key={ev.id} className="flex items-start gap-3">
          <div className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "var(--fop-primary)" }} />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-[var(--fop-text)] flex items-center gap-2 flex-wrap">
              <span>{ev.label}</span>
              <ChevronRight size={11} className="text-[var(--fop-text-4)]" />
              <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                {fmtDate(ev.at)}
              </span>
            </div>
            {(ev.actor_email || ev.details) && (
              <div className="text-xs text-[var(--fop-text-3)] mt-0.5 truncate">
                {ev.actor_email && <span className="font-mono">{ev.actor_email}</span>}
                {ev.details && <> · {ev.details}</>}
              </div>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--fop-text-3)] mb-2.5">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] mb-1">{label}</div>
      {children}
    </label>
  );
}
